import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatchActions, useAppState } from '../../context/AppStateProvider/useAppState';
import {
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    type SelectChangeEvent,
    Box,
    Typography,
} from '@mui/material';
import { Icon } from '../Icons/icons';

const AmenityFilter: React.FC<{}> = ({}) => {
    const { state } = useAppState();
    const { selectedAmenityTypes, amenityTypes, amenities } = state;
    const { setSelectedAmenityTypes } = useAppDispatchActions();
    const [open, setOpen] = useState(false);

    const filteredAmenityTypes = useMemo(() => {
        return amenityTypes.filter((type) => {
            const hasLocalAmenities = amenities.some((a) => a.type === type.slug);
            const hasPoiConfig = type.poi && type.poi.length > 0;
            return hasLocalAmenities || hasPoiConfig;
        });
    }, [amenityTypes, amenities]);

    const trackAmenityItemClick = (name: string) => {
        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
        (window as any).dataCoreLayer.push({
            event: 'od_planner_list_click_facilities',
            click_text: name,
            click_classes: 'amenity',
        });
    };

    useEffect(() => {
        if (!open) return;
        // --- GTM TRACKING START ---
        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];

        (window as any).dataCoreLayer.push({
            event: 'od_planner_list_click_facilities_button',
        });
        // --- GTM TRACKING END ---
    }, [open]);

    return (
        <FormControl
            size='small'
            sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 10,
            }}>
            <Select
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                multiple
                displayEmpty
                value={selectedAmenityTypes}
                onChange={(event: SelectChangeEvent<unknown>) => {
                    const {
                        target: { value },
                    } = event;

                    // Handle "Clear all" selection
                    const selectedValues = typeof value === 'string' ? value.split(',') : (value as string[]);
                    if (selectedValues.includes('__CLEAR_ALL__')) {
                        setSelectedAmenityTypes([]);
                        return;
                    }

                    setSelectedAmenityTypes(selectedValues);
                }}
                renderValue={() => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className='inline-flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full'>
                            <svg
                                width='16'
                                height='12'
                                viewBox='0 0 16 12'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'>
                                <path
                                    d='M13.2414 5.65657C13.2414 5.95661 13.1542 6.24437 12.999 6.45653C12.8438 6.66869 12.6333 6.78788 12.4138 6.78788H3.58621C3.36672 6.78788 3.15622 6.66869 3.00101 6.45653C2.84581 6.24437 2.75862 5.95661 2.75862 5.65657C2.75862 5.35653 2.84581 5.06877 3.00101 4.85661C3.15622 4.64445 3.36672 4.52526 3.58621 4.52526H12.4138C12.6333 4.52526 12.8438 4.64445 12.999 4.85661C13.1542 5.06877 13.2414 5.35653 13.2414 5.65657ZM15.1724 0H0.827586C0.608097 0 0.397597 0.119192 0.242394 0.331354C0.0871919 0.543517 0 0.831271 0 1.13131C0 1.43136 0.0871919 1.71911 0.242394 1.93127C0.397597 2.14344 0.608097 2.26263 0.827586 2.26263H15.1724C15.3919 2.26263 15.6024 2.14344 15.7576 1.93127C15.9128 1.71911 16 1.43136 16 1.13131C16 0.831271 15.9128 0.543517 15.7576 0.331354C15.6024 0.119192 15.3919 0 15.1724 0ZM9.65517 9.05051H6.34483C6.12534 9.05051 5.91484 9.1697 5.75964 9.38187C5.60443 9.59403 5.51724 9.88178 5.51724 10.1818C5.51724 10.4819 5.60443 10.7696 5.75964 10.9818C5.91484 11.1939 6.12534 11.3131 6.34483 11.3131H9.65517C9.87466 11.3131 10.0852 11.1939 10.2404 10.9818C10.3956 10.7696 10.4828 10.4819 10.4828 10.1818C10.4828 9.88178 10.3956 9.59403 10.2404 9.38187C10.0852 9.1697 9.87466 9.05051 9.65517 9.05051Z'
                                    fill='white'
                                />
                            </svg>
                        </div>
                        <div className='text-sm leading-tight font-bold text-[var(--colour-white)]'>Filter</div>
                    </Box>
                )}
                sx={{
                    'borderRadius': '9999px',
                    'backgroundColor': 'var(--colour-charcoal-dark)',
                    'color': 'white',
                    'transition': 'background-color 0.2s',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid var(--colour-charcoal-dark)',
                    },
                    '&:hover': {
                        'backgroundColor': 'var(--colour-gold)',
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: '1px solid var(--colour-charcoal-dark)',
                        },
                    },
                    '& .MuiSelect-select': {
                        padding: '4px 16px 4px 5px',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '28px',
                    },
                    '& .MuiSelect-icon': {
                        color: 'white',
                        right: '8px',
                    },
                }}
                MenuProps={{
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'right',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                    slotProps: {
                        paper: {
                            sx: {
                                'mt': 1,
                                'p': 2,
                                'width': 300,
                                'borderRadius': '12px',
                                '& .MuiMenuItem-root': {
                                    // Remove all padding on the MenuItem itself
                                    padding: 0,
                                },
                                '& .MuiListItemText-primary': {
                                    fontSize: '16px',
                                },
                            },
                        },
                    },
                }}>
                {filteredAmenityTypes.map((type) => (
                    <MenuItem
                        key={type.slug}
                        value={type.slug}
                        onClick={() => trackAmenityItemClick(type.name)}>
                        <Checkbox checked={selectedAmenityTypes.indexOf(type.slug) > -1} />
                        {type.icon_id && !type.icon && (
                            <img
                                src={`https://api.mazemap.com/api/icons/${type.icon_id}.svg`}
                                alt=''
                                style={{ width: 24, height: 24, marginRight: 8 }}
                            />
                        )}
                        {type.icon && (
                            <Icon
                                name={type.icon}
                                size={24}
                                style={{ marginRight: 8 }}
                            />
                        )}
                        <ListItemText primary={type.name} />
                    </MenuItem>
                ))}

                <MenuItem
                    value='__CLEAR_ALL__'
                    onClick={() => trackAmenityItemClick('Clear all')}
                    sx={{
                        borderTop: '1px solid #e0e0e0',
                        paddingTop: '8px !important',
                        marginTop: '8px',
                        fontWeight: 'bold',
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                visibility: selectedAmenityTypes.length > 0 ? 'visible' : 'hidden',
                            }}>
                            <Checkbox
                                checked={false}
                                sx={{ visibility: 'hidden' }}
                            />
                            <ListItemText
                                primary='Clear all'
                                slotProps={{ primary: { fontWeight: 'bold', fontSize: '16px' } }}
                            />
                        </Box>
                        <Typography
                            component='span'
                            variant='body1'
                            sx={{
                                'cursor': 'pointer',
                                'fontWeight': 'bold',
                                'mr': 2,
                                'fontSize': '16px',
                                'color': 'var(--colour-charcoal-dark)',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen(false);
                            }}>
                            Close
                        </Typography>
                    </Box>
                </MenuItem>
            </Select>
        </FormControl>
    );
};

export default AmenityFilter;
